import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
  Space,
  Button,
  Typography,
  Divider,
  Alert,
  Spin,
  Tag,
  Switch,
  message,
} from 'antd';
import { PlusOutlined, ArrowLeftOutlined, BarChartOutlined } from '@ant-design/icons';
import { Notice } from '../../components/common/Notice';
import { surveyService } from '../../services/survey.service';
import { RichTextEditor } from '../../components/common/RichTextEditor';
import { contractorService } from '../../services/contractor.service';
import { profileService, Country } from '../../services/profile.service';
import { validationService } from '../../services/validation.service';
import { ValidationLevel } from '../../types/validation.types';
import type { SurveyTagOption } from '../../types/tag.types';
import SurveyMinLevelField from '../../components/contractor/SurveyMinLevelField';
import { parseApiError } from '../../utils/error-handler';
import {
  Survey,
  Question,
  GeographicScope,
  SurveyStatus,
  SurveyVisibilityResponse,
  CreateSurveyData,
} from '../../types/survey.types';
import { Contractor } from '../../types/contractor.types';
import { State, City } from '../../types/profile.types';
import { QuestionBuilderModal } from '../../components/survey/QuestionBuilderModal';
import { SurveyExportButton } from '../../components/admin/surveys/SurveyExportButton';
import { SurveyLocationCard } from '../../components/survey/SurveyLocationCard';
import { QuestionGroupEditor } from '../../components/survey/QuestionGroupEditor';
import { surveyQuestionGroupService } from '../../services/survey-question-group.service';
import { QuestionGroupDetail } from '../../types/survey-question-group.types';
import {
  SurveyVisibilityLevel,
  isDateBasedVisibilityLevel,
} from '../../types/survey-visibility.types';

const VISIBILITY_OPTIONS: Array<{ value: SurveyVisibilityLevel; label: string }> = [
  { value: 'PUBLIC_AFTER_CLOSE', label: 'Público, após encerramento' },
  { value: 'PUBLIC_AFTER_DATE', label: 'Público, após data a definir' },
  { value: 'PARTICIPANTS_AFTER_CLOSE', label: 'Apenas Participantes, após Encerramento' },
  { value: 'PARTICIPANTS_AFTER_DATE', label: 'Apenas Participantes, após data a definir' },
  { value: 'CONTRACTOR_ONLY', label: 'Apenas Contratante' },
];

// Phase 4 - US2: Survey Detail with Question Builder (T083, T084)

export const SurveyDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionGroups, setQuestionGroups] = useState<QuestionGroupDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showQuestionBuilder, setShowQuestionBuilder] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [saving, setSaving] = useState(false);
  const [minimumValidationLevel, setMinimumValidationLevel] = useState<ValidationLevel>('LEVEL_1');
  const [effectiveValidationLevel, setEffectiveValidationLevel] = useState<ValidationLevel>('LEVEL_1');
  const [platformFloorApplied, setPlatformFloorApplied] = useState(false);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [countrySearchText, setCountrySearchText] = useState<string>('');
  const [stateSearchText, setStateSearchText] = useState<string>('');
  const [citySearchText, setCitySearchText] = useState<string>('');
  const [searchingCountries, setSearchingCountries] = useState(false);
  const [searchingStates, setSearchingStates] = useState(false);
  const [searchingCities, setSearchingCities] = useState(false);
  const [brazilCountryId, setBrazilCountryId] = useState<string>('');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [countryInputValue, setCountryInputValue] = useState<string>('');
  const [stateInputValue, setStateInputValue] = useState<string>('');
  const [cityInputValue, setCityInputValue] = useState<string>('');
  const [visibilityLevel, setVisibilityLevel] = useState<SurveyVisibilityLevel>('PUBLIC_AFTER_CLOSE');
  const [availableTags, setAvailableTags] = useState<SurveyTagOption[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [formData, setFormData] = useState<
    {
      name: string;
      description: string;
      geographicScope: GeographicScope;
      countryId?: string;
      stateId?: string;
      cityId?: string;
      contractorId: string;
      surveyValue: number;
      allowGuestParticipation: boolean;
      isElectoral: boolean;
      startDate?: Dayjs | null;
      endDate?: Dayjs | null;
      status?: SurveyStatus;
      visibilityLevel?: SurveyVisibilityLevel;
      disclosureDate?: Dayjs | null;
    }
  >({
    name: '',
    description: '',
    startDate: undefined,
    endDate: undefined,
    geographicScope: GeographicScope.NATIONAL,
    contractorId: '',
    surveyValue: 0,
    allowGuestParticipation: false,
    isElectoral: false,
  });

  useEffect(() => {
    if (isNew) {
      setSurvey(null);
      setQuestions([]);
      setLoading(false);
      return;
    }

    loadSurvey();
    loadQuestions();
    loadQuestionGroups();
  }, [id, isNew]);

  useEffect(() => {
    const fetchContractors = async () => {
      try {
        const result = await contractorService.listContractors({ page: 1, limit: 50 });
        setContractors(result.data);
      } catch (err) {
        console.error('Failed to load contractors', err);
      }
    };

    const findBrazil = async () => {
      try {
        // Search for Brazil to auto-fill countryId
        const countries = await profileService.getCountries('brasil');
        if (countries.length > 0) {
          const brazil = countries[0];
          setBrazilCountryId(brazil.id);
          setSelectedCountry(brazil);
          setCountryInputValue(brazil.name);
          // Auto-fill country field if empty
          if (!formData.countryId && formData.geographicScope === GeographicScope.NATIONAL) {
            setFormData(prev => ({ ...prev, countryId: brazil.id }));
          }
        }
      } catch (err) {
        console.error('Failed to find Brazil', err);
      }
    };

    fetchContractors();
    findBrazil();
  }, []);

  useEffect(() => {
    const loadTags = async () => {
      try {
        const tags = await surveyService.getActiveSurveyTags();
        setAvailableTags(tags);
      } catch {
        // ignore
      }
    };

    loadTags();
  }, []);

  const loadSurvey = async () => {
    try {
      const data = await surveyService.getSurvey(id!);
      const visibilityData: SurveyVisibilityResponse = await surveyService.getSurveyVisibility(id!);
      setSurvey(data);
      setSelectedTagIds((data.tags || []).map((tag) => tag.id));
      setVisibilityLevel(visibilityData.visibilityLevel);
      setFormData({
        name: data.name,
        description: data.description,
        startDate: data.startDate ? dayjs(data.startDate) : undefined,
        endDate: data.endDate ? dayjs(data.endDate) : undefined,
        geographicScope: data.geographicScope,
        countryId: data.countryId,
        stateId: data.stateId,
        cityId: data.cityId,
        contractorId: data.contractorId,
        surveyValue: data.surveyValue,
        allowGuestParticipation: !!data.allowGuestParticipation,
        isElectoral: !!data.isElectoral,
        status: data.status,
        visibilityLevel: visibilityData.visibilityLevel,
        disclosureDate: visibilityData.disclosureDate ? dayjs(visibilityData.disclosureDate) : undefined,
      });
      
      // Load geographic data for display
      if ((data as any).country) {
        setSelectedCountry((data as any).country);
        setCountryInputValue((data as any).country.name);
      }
      // Only hydrate state/city when the saved scope actually requires them. A survey
      // saved with a stray stateId/cityId (e.g. scope was later changed to NATIONAL
      // without clearing them) must not be shown as if it still had that location set,
      // otherwise the disabled input is stuck displaying a value the user can't remove.
      if (
        (data.geographicScope === GeographicScope.STATE || data.geographicScope === GeographicScope.CITY) &&
        (data as any).state
      ) {
        setSelectedState((data as any).state);
        setStateInputValue(`${(data as any).state.name} (${(data as any).state.code})`);
      }
      if (data.geographicScope === GeographicScope.CITY && (data as any).city) {
        setSelectedCity((data as any).city);
        setCityInputValue((data as any).city.name);
      }

      try {
        const policy = await validationService.getSurveyValidationPolicy(id!);
        setMinimumValidationLevel(policy.minimumValidationLevel);
        setEffectiveValidationLevel(policy.effectiveLevel);
        setPlatformFloorApplied(policy.platformFloorApplied);
      } catch {
        setMinimumValidationLevel('LEVEL_1');
        setEffectiveValidationLevel('LEVEL_1');
        setPlatformFloorApplied(false);
      }
    } catch (err: any) {
      const apiError = parseApiError(err);
      setError(apiError.message);
    } finally {
      setLoading(false);
    }
  };

  const loadQuestions = async () => {
    try {
      const data = await surveyService.getQuestions(id!);
      setQuestions(data);
    } catch (err: any) {
      const apiError = parseApiError(err);
      console.error('Failed to load questions:', apiError.message);
    }
  };

  const loadQuestionGroups = async () => {
    if (!id || id === 'new') return;
    try {
      const items = await surveyQuestionGroupService.listGroupsAdmin(id);
      setQuestionGroups(items);
    } catch {
      // groups are optional — silently ignore errors
    }
  };

  // Reload groups every time the question builder modal opens so new groups are visible
  useEffect(() => {
    if (showQuestionBuilder) {
      loadQuestionGroups();
    }
  }, [showQuestionBuilder]);

  // Country search with debounce
  useEffect(() => {
    if (formData.geographicScope !== GeographicScope.NATIONAL) return;
    
    if (countrySearchText.length >= 2 || countrySearchText.length === 0) {
      const timer = setTimeout(() => {
        searchCountries(countrySearchText);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setCountries([]);
    }
  }, [countrySearchText, formData.geographicScope]);

  // State search with debounce
  useEffect(() => {
    if (!formData.countryId || formData.geographicScope !== GeographicScope.STATE) return;
    
    if (stateSearchText.length >= 2 || stateSearchText.length === 0) {
      const timer = setTimeout(() => {
        searchStates(formData.countryId as string, stateSearchText);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setStates([]);
    }
  }, [stateSearchText, formData.countryId, formData.geographicScope]);

  // City search with debounce
  useEffect(() => {
    if (!formData.stateId || formData.geographicScope !== GeographicScope.CITY) return;
    
    if (citySearchText.length >= 2 || citySearchText.length === 0) {
      const timer = setTimeout(() => {
        searchCities(formData.stateId as string, citySearchText);
      }, 300);
      
      return () => clearTimeout(timer);
    } else {
      setCities([]);
    }
  }, [citySearchText, formData.stateId, formData.geographicScope]);

  const searchCountries = async (searchText: string) => {
    try {
      setSearchingCountries(true);
      const data = await profileService.getCountries(searchText);
      setCountries(data);
    } catch (error) {
      const apiError = parseApiError(error);
      console.error('Failed to search countries:', apiError.message);
    } finally {
      setSearchingCountries(false);
    }
  };

  const searchStates = async (countryId: string, searchText: string) => {
    try {
      setSearchingStates(true);
      const data = await profileService.getStatesByCountry(countryId, searchText);
      setStates(data);
    } catch (error) {
      const apiError = parseApiError(error);
      console.error('Failed to search states:', apiError.message);
    } finally {
      setSearchingStates(false);
    }
  };

  const searchCities = async (stateId: string, searchText: string) => {
    try {
      setSearchingCities(true);
      const data = await profileService.getCitiesByState(stateId, searchText);
      setCities(data);
    } catch (error) {
      const apiError = parseApiError(error);
      console.error('Failed to search cities:', apiError.message);
    } finally {
      setSearchingCities(false);
    }
  };

  const handleInputChange = (
    field:
      | keyof CreateSurveyData
      | 'status'
      | 'startDate'
      | 'endDate'
      | 'visibilityLevel'
      | 'disclosureDate'
      | 'allowGuestParticipation'
      | 'isElectoral',
    value: string | number | boolean | GeographicScope | SurveyStatus | SurveyVisibilityLevel | Dayjs | null | undefined
  ) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // When scope changes, clear dependent fields
      if (field === 'geographicScope') {
        if (value === GeographicScope.NATIONAL) {
          // Auto-fill Brazil if we found it
          if (brazilCountryId) {
            updated.countryId = brazilCountryId;
          }
          updated.stateId = '';
          updated.cityId = '';
          setSelectedState(null);
          setSelectedCity(null);
          setStateInputValue('');
          setCityInputValue('');
        } else if (value === GeographicScope.STATE) {
          updated.cityId = '';
          setCountrySearchText('');
          setCountries([]);
          setSelectedCity(null);
          setCityInputValue('');
        } else if (value === GeographicScope.CITY) {
          setCountrySearchText('');
          setCountries([]);
        }
      }

      // When country changes, clear dependent fields
      if (field === 'countryId') {
        updated.stateId = '';
        updated.cityId = '';
        setStateSearchText('');
        setStates([]);
        setCitySearchText('');
        setCities([]);
        setSelectedState(null);
        setSelectedCity(null);
        setStateInputValue('');
        setCityInputValue('');
      }

      // When state changes, clear city field
      if (field === 'stateId') {
        updated.cityId = '';
        setCitySearchText('');
        setCities([]);
        setSelectedCity(null);
        setCityInputValue('');
      }

      if (field === 'visibilityLevel') {
        const nextVisibilityLevel = value as SurveyVisibilityLevel;
        setVisibilityLevel(nextVisibilityLevel);
        if (!isDateBasedVisibilityLevel(nextVisibilityLevel)) {
          updated.disclosureDate = undefined;
        }
      }

      return updated;
    });
  };

  const handleCountrySearch = (value: string) => {
    setCountrySearchText(value);
    setCountryInputValue(value);
  };

  const handleCountrySelect = (value: string) => {
    const country = countries.find(c => c.id === value);
    if (country) {
      setSelectedCountry(country);
      setCountryInputValue(country.name);
      handleInputChange('countryId', value);
    }
  };

  const handleStateSearch = (value: string) => {
    setStateSearchText(value);
    setStateInputValue(value);
  };

  const handleStateSelect = (value: string) => {
    const state = states.find(s => s.id === value);
    if (state) {
      setSelectedState(state);
      setStateInputValue(`${state.name} (${state.code})`);
      handleInputChange('stateId', value);
    }
  };

  const handleCitySearch = (value: string) => {
    setCitySearchText(value);
    setCityInputValue(value);
  };

  const handleCitySelect = (value: string) => {
    const city = cities.find(c => c.id === value);
    if (city) {
      setSelectedCity(city);
      setCityInputValue(city.name);
      handleInputChange('cityId', value);
    }
  };

  const buildPayload = () => {
    if (!formData.name || !formData.startDate || !formData.endDate || !formData.contractorId) {
      throw new Error('Preencha nome, datas e contratante.');
    }

    const payload: any = {
      name: formData.name,
      description: formData.description,
      startDate: formData.startDate.toISOString(),
      endDate: formData.endDate.toISOString(),
      geographicScope: formData.geographicScope,
      contractorId: formData.contractorId,
      surveyValue: Number(formData.surveyValue) || 0,
      visibilityLevel: formData.visibilityLevel || visibilityLevel,
      disclosureDate: isDateBasedVisibilityLevel(formData.visibilityLevel || visibilityLevel)
        ? formData.disclosureDate?.format('YYYY-MM-DD')
        : null,
    };

    // Add optional geographic fields only if they have values
    if (formData.countryId) payload.countryId = formData.countryId;
    if (formData.stateId) payload.stateId = formData.stateId;
    if (formData.cityId) payload.cityId = formData.cityId;
    if (formData.status) payload.status = formData.status;

    payload.allowGuestParticipation = formData.allowGuestParticipation;
    payload.isElectoral = formData.isElectoral;

    return payload as CreateSurveyData & { status?: SurveyStatus };
  };

  const handleSaveSurvey = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      setSaving(true);
      const payload = buildPayload();

      if (isNew) {
        const created = await surveyService.createSurvey(payload);

        if (selectedTagIds.length > 0) {
          await surveyService.updateAdminSurveyTags(created.id, { tagIds: selectedTagIds });
        }

        message.success('Pesquisa criada com sucesso!');
        navigate(`/admin/surveys/${created.id}`);
      } else {
        const updated = await surveyService.updateSurvey(id!, payload);
        await surveyService.updateAdminSurveyTags(id!, { tagIds: selectedTagIds });
        setSurvey(updated);
        await validationService.upsertSurveyValidationPolicy(id!, { minimumValidationLevel });
        message.success('Pesquisa atualizada com sucesso!');
      }
    } catch (err: any) {
      const apiError = parseApiError(err);
      message.error(apiError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta questão?')) return;

    try {
      await surveyService.deleteQuestion(id!, questionId);
      message.success('Questão excluída com sucesso!');
      loadQuestions();
    } catch (err: any) {
      const apiError = parseApiError(err);
      message.error(apiError.message);
    }
  };

  if (loading) return <Spin />;
  if (error) return <Alert type="error" message={error} />;

  // Check if questions can be managed
  const hasResponses = survey?.participantCount && survey.participantCount > 0;
  const isActive = survey?.status === SurveyStatus.ACTIVE || survey?.status === SurveyStatus.COMPLETED || survey?.status === SurveyStatus.PAUSED;
  const canManageQuestions = !isNew && survey && !hasResponses && !isActive;

  const countryOptions = [
    ...(selectedCountry
      ? [{ value: selectedCountry.id, label: `${selectedCountry.name} (${selectedCountry.code})` }]
      : []),
    ...countries
      .filter((c) => !selectedCountry || c.id !== selectedCountry.id)
      .map((c) => ({ value: c.id, label: `${c.name} (${c.code})` })),
  ];

  const stateOptions = [
    ...(selectedState
      ? [{ value: selectedState.id, label: `${selectedState.name} (${selectedState.code})` }]
      : []),
    ...states
      .filter((s) => !selectedState || s.id !== selectedState.id)
      .map((s) => ({ value: s.id, label: `${s.name} (${s.code})` })),
  ];

  const cityOptions = [
    ...(selectedCity ? [{ value: selectedCity.id, label: selectedCity.name }] : []),
    ...cities
      .filter((c) => !selectedCity || c.id !== selectedCity.id)
      .map((c) => ({ value: c.id, label: c.name })),
  ];

  const selectedInactiveTags = (survey?.tags || []).filter(
    (tag) => tag.status === 'INACTIVE' && selectedTagIds.includes(tag.id),
  );

  const tagOptions = [
    ...availableTags.map((tag) => ({
      value: tag.id,
      label: tag.name,
    })),
    ...selectedInactiveTags
      .filter((inactiveTag) => !availableTags.some((activeTag) => activeTag.id === inactiveTag.id))
      .map((inactiveTag) => ({
        value: inactiveTag.id,
        label: `${inactiveTag.name} (inativa)`,
      })),
  ];

  const hasInactiveSelection = selectedInactiveTags.length > 0;

  if (!isNew && !survey) return <Notice type="warning" message="Pesquisa não encontrada" />;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {isNew ? 'Nova Pesquisa' : survey?.name}
        </Typography.Title>
        <Space>
          {!isNew && survey && (
            <SurveyExportButton surveyId={survey.id} surveyName={survey.name} />
          )}
          {!isNew && survey?.status === SurveyStatus.COMPLETED && (
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/admin/surveys/${id}/analysis`)}
            >
              Ver Análise
            </Button>
          )}
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/surveys')}>
            Voltar
          </Button>
          <Button type="primary" onClick={() => handleSaveSurvey()} loading={saving}>
            {isNew ? 'Criar pesquisa' : 'Salvar alterações'}
          </Button>
        </Space>
      </Space>

      <Card title="Dados da Pesquisa">
        <Form layout="vertical" onSubmitCapture={handleSaveSurvey}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Nome" required>
                <Input
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Digite o nome da pesquisa"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Contratante" required>
                <Select
                  value={formData.contractorId || ''}
                  onChange={(value) => handleInputChange('contractorId', value)}
                  placeholder="Selecione o contratante"
                >
                  {contractors.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {c.tradeName || c.legalName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="Descrição">
                <RichTextEditor
                  value={formData.description || ''}
                  onChange={(val) => handleInputChange('description', val)}
                  placeholder="Descreva o objetivo e contexto da pesquisa..."
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Início" required>
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  value={formData.startDate || null}
                  onChange={(value) => handleInputChange('startDate', value)}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Fim" required>
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  value={formData.endDate || null}
                  onChange={(value) => handleInputChange('endDate', value)}
                  format="YYYY-MM-DD HH:mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Valor da pesquisa (R$)">
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  step={0.01}
                  value={formData.surveyValue ?? 0}
                  onChange={(value) => handleInputChange('surveyValue', value || 0)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Pesquisa eleitoral">
                <Switch
                  checked={formData.isElectoral}
                  onChange={(checked) => handleInputChange('isElectoral', checked)}
                  checkedChildren="Ativado"
                  unCheckedChildren="Desativado"
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Nível de Visibilidade dos Resultados"
                required
              >
                <Select
                  value={formData.visibilityLevel || visibilityLevel}
                  placeholder="Selecione o nível de visibilidade"
                  onChange={(value) => handleInputChange('visibilityLevel', value as SurveyVisibilityLevel)}
                >
                  {VISIBILITY_OPTIONS.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Data da Divulgação"
                required={isDateBasedVisibilityLevel(formData.visibilityLevel || visibilityLevel)}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  disabled={!isDateBasedVisibilityLevel(formData.visibilityLevel || visibilityLevel)}
                  value={formData.disclosureDate || null}
                  onChange={(value) => handleInputChange('disclosureDate', value)}
                />
              </Form.Item>
              <div style={{ color: '#999', fontSize: 12, marginTop: -16 }}>
                Campo usado em "Público, após data a definir" e "Apenas Participantes, após data a definir".
              </div>
            </Col>

            <Col span={24}>
              <Form.Item
                label="Etiquetas da Pesquisa"
                validateStatus={hasInactiveSelection ? 'warning' : undefined}
                help={
                  hasInactiveSelection
                    ? 'Há etiqueta(s) inativa(s) associada(s). Remova ou substitua por etiquetas ativas para salvar.'
                    : 'Selecione até 2 etiquetas ativas para a pesquisa.'
                }
              >
                <Select
                  mode="multiple"
                  allowClear
                  maxCount={2}
                  value={selectedTagIds}
                  onChange={(values) => setSelectedTagIds(values.slice(0, 2))}
                  options={tagOptions}
                  placeholder="Selecione até 2 etiquetas"
                />
              </Form.Item>
            </Col>
          </Row>

          {!isNew && (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item label="Status">
                  <Select
                    value={formData.status || ''}
                    onChange={(value) => handleInputChange('status', value as SurveyStatus)}
                  >
                    <Select.Option value="">--</Select.Option>
                    <Select.Option value={SurveyStatus.DRAFT}>Rascunho</Select.Option>
                    <Select.Option value={SurveyStatus.SCHEDULED}>Agendada</Select.Option>
                    <Select.Option value={SurveyStatus.ACTIVE}>Ativa</Select.Option>
                    <Select.Option value={SurveyStatus.PAUSED}>Pausada</Select.Option>
                    <Select.Option value={SurveyStatus.COMPLETED}>Concluída</Select.Option>
                    <Select.Option value={SurveyStatus.CANCELED}>Cancelada</Select.Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <SurveyMinLevelField
                  value={minimumValidationLevel}
                  effectiveLevel={effectiveValidationLevel}
                  platformFloorApplied={platformFloorApplied}
                  onChange={setMinimumValidationLevel}
                />
              </Col>

              <Col span={8}>
                <Form.Item label="Participação sem conta (OTP)">
                  <Switch
                    checked={formData.allowGuestParticipation}
                    onChange={(checked) => handleInputChange('allowGuestParticipation', checked)}
                    checkedChildren="Ativado"
                    unCheckedChildren="Desativado"
                  />
                </Form.Item>
              </Col>

            </Row>
          )}
        </Form>
      </Card>

      <SurveyLocationCard
        geographicScope={formData.geographicScope || GeographicScope.NATIONAL}
        showScopeSelector
        onScopeChange={(value) => handleInputChange('geographicScope', value)}
        countryInputValue={countryInputValue}
        stateInputValue={stateInputValue}
        cityInputValue={cityInputValue}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        cityOptions={cityOptions}
        countryDisabled={false}
        stateDisabled={!formData.countryId && formData.geographicScope === GeographicScope.STATE}
        cityDisabled={!formData.stateId && formData.geographicScope === GeographicScope.CITY}
        searchingCountries={searchingCountries}
        searchingStates={searchingStates}
        searchingCities={searchingCities}
        onCountrySearch={handleCountrySearch}
        onCountrySelect={handleCountrySelect}
        onStateSearch={handleStateSearch}
        onStateSelect={handleStateSelect}
        onCitySearch={handleCitySearch}
        onCitySelect={handleCitySelect}
        countryHelperText={
          formData.geographicScope === GeographicScope.NATIONAL
            ? 'Digite pelo menos 2 caracteres para buscar'
            : 'Opcional para abrangência Estadual/Municipal'
        }
        stateHelperText={
          !formData.countryId && formData.geographicScope === GeographicScope.STATE
            ? 'Selecione um país primeiro para buscar'
            : 'Digite pelo menos 2 caracteres para buscar (opcional)'
        }
        cityHelperText={
          !formData.stateId && formData.geographicScope === GeographicScope.CITY
            ? 'Selecione um estado primeiro para buscar'
            : 'Digite pelo menos 2 caracteres para buscar (opcional)'
        }
      />

      <Divider />

      {!isNew && survey ? (
        <>
          {!canManageQuestions && (
            <Notice
              type="warning"
              message="Não é possível alterar as questões"
              description={
                hasResponses
                  ? 'Esta pesquisa já possui respostas. As questões não podem ser alteradas.'
                  : isActive
                  ? 'Esta pesquisa está ativa ou concluída. As questões não podem ser alteradas.'
                  : 'Salve a pesquisa para habilitar o cadastro de questões.'
              }
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Card
            title={`Questões (${questions.length})`}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                disabled={!canManageQuestions}
                onClick={() => {
                  setEditingQuestion(null);
                  setShowQuestionBuilder(true);
                }}
              >
                Adicionar Questão
              </Button>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {questions.map((question) => (
                <Card key={question.id} size="small">
                  <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space direction="vertical" size={4}>
                      <Typography.Text strong>#{question.order} - {question.shortDescription}</Typography.Text>
                      <Space size={4} wrap>
                        <Tag>{question.questionType}</Tag>
                        {question.required && <Tag color="red">Obrigatória</Tag>}
                        {(() => {
                          const group = questionGroups.find(
                            (g) => String(g.id) === String((question as any).questionGroupId),
                          );
                          if (!group) return null;
                          return (
                            <Tag color={group.scope === 'NATIONAL' ? 'blue' : 'purple'}>
                              {group.name}
                              {group.scope === 'STATE' && group.stateName ? ` — ${group.stateName}` : ''}
                            </Tag>
                          );
                        })()}
                      </Space>
                      {question.longDescription && (
                        <Typography.Paragraph style={{ marginBottom: 4 }}>
                          {question.longDescription}
                        </Typography.Paragraph>
                      )}
                    </Space>
                    <Space>
                      <Button
                        size="small"
                        disabled={!canManageQuestions}
                        onClick={() => {
                          setEditingQuestion(question);
                          setShowQuestionBuilder(true);
                        }}
                      >
                        Editar
                      </Button>
                      <Button
                        size="small"
                        danger
                        disabled={!canManageQuestions}
                        onClick={() => handleDeleteQuestion(question.id)}
                      >
                        Excluir
                      </Button>
                    </Space>
                  </Space>
                </Card>
              ))}

            </Space>
          </Card>
        </>
      ) : (
        <Notice type="info" message="Salve a pesquisa para habilitar o cadastro de questões." />
      )}

      {!isNew && id && (
        <Card title="Grupos de Perguntas" style={{ marginTop: 0 }}>
          <QuestionGroupEditor
            surveyId={id}
            surveyScope={formData.geographicScope}
            isAdmin={true}
          />
        </Card>
      )}

      <QuestionBuilderModal
        surveyId={id!}
        question={editingQuestion}
        nextOrder={questions.length + 1}
        open={showQuestionBuilder}
        availableGroups={questionGroups}
        surveyIsElectoral={Boolean(survey?.isElectoral)}
        onSave={() => {
          setShowQuestionBuilder(false);
          loadQuestions();
        }}
        onCancel={() => setShowQuestionBuilder(false)}
      />
    </Space>
  );
};
